// Riverstone (decision 16; docs/design/riverstone.md), the default token set. Names and values are
// the design language's own (--ground, --ink-2, --r-stone, --sh-stone …), mirrored in
// site/shared/theme.css; a test keeps the two in step. The product's semantic roles (bg, text,
// primary …) are mapped onto them below. Where the spec is silent (heading line heights, overlay,
// durations other than 160 ms) the choice is noted.

import type { Brand, TokenSet } from '../schema.ts';
import { OFFICE_COMPONENTS, officeMeaning } from './office.ts';

/** The company colour Riverstone shows when a company has not set its own. */
export const RIVERSTONE_DEFAULT_BRAND: Brand = { primary: '#257570' };

const display = (size: number, weight = 800) => ({
  family: 'display' as const,
  size,
  lineHeight: 1.1,
  weight,
  letterSpacing: -0.02,
});

/** Riverstone colour tokens that exist in both modes, as palette steps (light.ink-2, dark.ink-2). */
const MODE_TOKENS = [
  'ground',
  'surface',
  'surface-2',
  'water',
  'water-2',
  'ink',
  'ink-2',
  'muted',
  'line',
  'field-border',
  'sun-soft',
  'ok',
  'ok-soft',
  'warn',
  'warn-soft',
  'bad',
  'bad-soft',
  'off',
  'off-soft',
  'info',
  'info-soft',
] as const;

function designRoles(mode: 'light' | 'dark'): Record<string, string> {
  return {
    ...Object.fromEntries(MODE_TOKENS.map((t) => [t, `${mode}.${t}`])),
    sun: 'fixed.sun',
    'sun-ink': 'fixed.sun-ink',
    brand: 'brand.primary',
    'brand-ink': 'brand.onPrimary',
    // Brand-coloured text (links, chip.brand): the brand itself in light mode, half-way to ink in dark.
    'brand-text': mode === 'light' ? 'brand.primary' : 'mix(brand.primary, dark.ink, 0.5)',
    star: `${mode}.warn`, // rating stars
    owner: `${mode}.info`, // owner-only items
    'owner-soft': `${mode}.info-soft`,
    scrim: mode === 'light' ? 'fixed.scrim-ink/0.45' : 'fixed.black/0.6', // behind sheets and modals
    'brand-soft': `mix(brand.primary, ${mode}.surface, 0.13)`, // color-mix(in srgb, brand 13%, surface)
    'brand-line': `mix(brand.primary, ${mode}.line, 0.4)`,
    // The camera is always dark, like the iOS camera; printed paper (PDF previews) is always light.
    'cam-bg': mode === 'light' ? 'fixed.cam-bg' : 'fixed.cam-bg-dark',
    'cam-ink': 'fixed.cam-ink',
    'cam-stamp': 'fixed.cam-stamp',
    paper: mode === 'light' ? 'fixed.paper' : 'fixed.paper-dark',
    'paper-ink': 'fixed.paper-ink',
    'paper-ink-2': 'fixed.paper-ink-2',
    // initials on a person's colour (.avatar), white in both modes
    'on-avatar': 'fixed.white',
  };
}

function productRoles(mode: 'light' | 'dark') {
  const on = mode === 'light' ? `${mode}.surface` : `${mode}.ground`; // label colour on status fills
  return {
    bg: `${mode}.ground`,
    surface: `${mode}.surface`,
    surfaceRaised: `${mode}.surface`,
    surfaceSunken: `${mode}.surface-2`,
    text: `${mode}.ink`,
    textMuted: `${mode}.muted`,
    border: `${mode}.line`,
    borderStrong: `${mode}.field-border`,
    focusRing: 'brand.primary', // focus outlines use full-strength --brand
    link: mode === 'light' ? 'brand.primary' : 'mix(brand.primary, dark.ink, 0.5)', // = --brand-text
    primary: 'brand.primary',
    primaryHover: 'brand.primaryHover',
    primaryPressed: 'brand.primaryPressed',
    onPrimary: 'brand.onPrimary',
    accent: 'fixed.sun',
    onAccent: 'fixed.sun-ink',
    success: `${mode}.ok`,
    onSuccess: on,
    warning: `${mode}.warn`,
    onWarning: on,
    danger: `${mode}.bad`,
    onDanger: on,
    info: `${mode}.info`,
    onInfo: on,
    overlay: mode === 'light' ? 'fixed.scrim-ink/0.45' : 'fixed.black/0.6', // = --scrim
  };
}

export const riverstone: TokenSet = {
  name: 'riverstone',
  version: '1.4.0',
  palette: {
    light: {
      ground: '#EAF0EB',
      surface: '#FFFFFF',
      'surface-2': '#F4F7F4',
      water: '#D7E8E3',
      'water-2': '#C6DFD9',
      ink: '#15302E',
      'ink-2': '#4E6461',
      muted: '#5D716A',
      line: '#D5DFD9',
      'field-border': '#828C86',
      'sun-soft': '#FCEFD2',
      ok: '#3D7843',
      'ok-soft': '#E3F1E4',
      warn: '#8F6200',
      'warn-soft': '#FBEFD3',
      bad: '#AF3F29',
      'bad-soft': '#F6DDD5',
      off: '#5B4BC4',
      'off-soft': '#EFEAFB',
      info: '#3B5BA9',
      'info-soft': '#E4EAF7',
    },
    dark: {
      ground: '#0F1A19',
      surface: '#172423',
      'surface-2': '#1D2C2A',
      water: '#1F3532',
      'water-2': '#2A4743',
      ink: '#E4EEEB',
      'ink-2': '#A9BDB8',
      muted: '#8FA5A0',
      line: '#2A3B39',
      'field-border': '#5C6F6C',
      'sun-soft': '#3A2E12',
      ok: '#7FC287',
      'ok-soft': '#18301C',
      warn: '#E3B04C',
      'warn-soft': '#352A10',
      bad: '#EE8D78',
      'bad-soft': '#3B1F19',
      off: '#A99CF2',
      'off-soft': '#262043',
      info: '#8FA8E6',
      'info-soft': '#1A2440',
    },
    // Sun and status colours never change per company (principle 5).
    fixed: {
      sun: '#F2B23E',
      'sun-ink': '#3D2A00',
      white: '#FFFFFF',
      black: '#000000',
      'shadow-ink': '#15302E',
      'scrim-ink': '#0F1A19',
      'cam-bg': '#0B1110',
      'cam-bg-dark': '#050808',
      'cam-ink': '#F2F6F4',
      'cam-stamp': '#FCEFD2',
      paper: '#FFFFFF',
      'paper-dark': '#F4F7F4',
      'paper-ink': '#15302E',
      'paper-ink-2': '#4E6461',
    },
  },
  // --brand-ink is white; the generator picks ink instead when white fails on a light company colour.
  onColorCandidates: ['fixed.white', 'light.ink'],
  color: {
    light: { ...productRoles('light'), ...designRoles('light') },
    dark: { ...productRoles('dark'), ...designRoles('dark') },
  },
  // Pairs Riverstone's components rely on, beyond the built-in ones.
  contrast: [
    { fg: 'ink-2', bg: 'ground', min: 4.5, why: 'secondary text on the page' },
    { fg: 'ink-2', bg: 'surface', min: 4.5, why: 'secondary text on a stone' },
    { fg: 'ink', bg: 'water', min: 4.5, why: 'text in lanes and trays' },
    { fg: 'bad', bg: 'surface', min: 4.5, why: 'a late time (.time.late) on a stone' },
    { fg: 'ok', bg: 'ok-soft', min: 4.5, why: 'chip.ok text' },
    { fg: 'warn', bg: 'warn-soft', min: 4.5, why: 'chip.warn text' },
    { fg: 'bad', bg: 'bad-soft', min: 4.5, why: 'chip.bad and btn.danger text' },
    { fg: 'off', bg: 'off-soft', min: 4.5, why: 'chip.off ("saved here") text' },
    { fg: 'info', bg: 'info-soft', min: 4.5, why: 'chip.info text' },
    { fg: 'sun-ink', bg: 'sun', min: 4.5, why: 'chip.sun and btn.sun text' },
    { fg: 'brand-text', bg: 'brand-soft', min: 4.5, why: 'chip.brand text' },
    { fg: 'cam-ink', bg: 'cam-bg', min: 4.5, why: 'camera controls and captions' },
    { fg: 'paper-ink-2', bg: 'paper', min: 4.5, why: 'secondary text on a PDF preview' },
    // The sun "now" ring is decorative (1.9:1); "Now" is always also written in text (spec).
  ],
  // .btn.primary:hover is brightness(1.06); :active only moves the button.
  brandDerive: { light: { hover: 0.03, pressed: 0 }, dark: { hover: 0.03, pressed: 0 } },
  typography: {
    families: {
      display: '"Gabarito", "Gabarito Fallback", system-ui, "Segoe UI", sans-serif',
      body: '"Inclusive Sans", "Inclusive Sans Fallback", system-ui, "Segoe UI", sans-serif',
      mono: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
    },
    // The company colour is the brand; fonts belong to the design language.
    brandFontSlots: [],
    allowedBrandFonts: [],
    // Arial (or its metric twins) resized to the web fonts' advance and vertical metrics, measured from
    // Gabarito 800 and Inclusive Sans 400 (hhea 940/-260 and 950/-250 per 1000), so the first frame
    // wraps like the loaded one. The same rules are in site/shared/theme.css.
    fallbackFaces: [
      {
        family: 'Gabarito Fallback',
        local: ['Arial', 'Arimo', 'Liberation Sans'],
        sizeAdjust: 92.58,
        ascentOverride: 101.53,
        descentOverride: 28.08,
        lineGapOverride: 0,
      },
      {
        family: 'Inclusive Sans Fallback',
        local: ['Arial', 'Arimo', 'Liberation Sans'],
        sizeAdjust: 104.65,
        ascentOverride: 90.78,
        descentOverride: 23.89,
        lineGapOverride: 0,
      },
    ],
    // The type scale measured from the reference screens (docs/design/fidelity-pass.md), named as
    // the prototypes name it (site/shared/theme.css --fs-*). The required roles repeat their twins:
    // h1 = title, h3 = card, label = cardSm, bodySm = small, caption = micro.
    scale: {
      display: { ...display(52), lineHeight: 0.98, letterSpacing: -0.025 }, // the one sheet headline (iPad)
      displayLg: { ...display(44), lineHeight: 1.05 }, // the iPad day's title
      title: { ...display(38), lineHeight: 1.05 }, // screen title (office)
      h1: { ...display(38), lineHeight: 1.05 },
      titleSm: { ...display(30), lineHeight: 1.05 }, // screen title on a phone
      h2: { family: 'display', size: 22, lineHeight: 1.15, weight: 800 }, // section heading
      cardLg: { family: 'display', size: 21, lineHeight: 1.15, weight: 700 }, // iPad card title, big buttons
      card: { family: 'display', size: 17, lineHeight: 1.15, weight: 700 }, // card title, pebble value
      h3: { family: 'display', size: 17, lineHeight: 1.15, weight: 700 },
      cardSm: { family: 'display', size: 15, lineHeight: 1.2, weight: 700 }, // buttons, tabs, lane pill title
      label: { family: 'display', size: 15, lineHeight: 1.2, weight: 700 },
      lede: { family: 'body', size: 16, lineHeight: 1.45, weight: 400 }, // the one line under a title
      body: { family: 'body', size: 14.5, lineHeight: 1.45, weight: 400 },
      doc: { family: 'body', size: 15, lineHeight: 1.6, weight: 400 }, // rendered documents, 72 characters
      small: { family: 'body', size: 13, lineHeight: 1.4, weight: 400 }, // secondary lines, place names
      bodySm: { family: 'body', size: 13, lineHeight: 1.4, weight: 400 },
      micro: { family: 'body', size: 12, lineHeight: 1.35, weight: 400 }, // pebble keys, captions
      caption: { family: 'body', size: 12, lineHeight: 1.35, weight: 400 },
      chip: { family: 'display', size: 12, lineHeight: 1.2, weight: 700 },
      time: { ...display(32), lineHeight: 1, tabularNums: true }, // river stones, the next stop, sheets
      timeLane: { ...display(16), lineHeight: 1.1, tabularNums: true }, // a dispatch lane pill's start (Amir, 2026-10-15)
      timeRow: { ...display(18), lineHeight: 1, letterSpacing: -0.01, tabularNums: true }, // a time leading an office list row
      timeNote: { family: 'display', size: 13, lineHeight: 1.3, weight: 700 },
      num: { ...display(30), lineHeight: 1, tabularNums: true }, // big numbers in stat stones
    },
  },
  radii: {
    'r-key': 7,
    'r-field': 18,
    'r-panel': 24,
    'r-stone': 30,
    'r-tray': 34,
    'r-sheet': 40,
    'r-pill': 999,
    'r-pebble-a': [22, 28, 20, 30],
    'r-pebble-b': [28, 20, 30, 22],
    'r-pebble-c': [20, 30, 24, 28],
    'r-logo': [13, 17, 12, 16],
    'r-bubble': [22, 22, 22, 6], // message bubbles
  },
  shape: {
    control: 'r-field',
    card: 'r-stone',
    chip: 'r-pill',
    sheet: 'r-sheet',
    button: 'r-pill',
    pebble: 'r-pebble-a',
    tray: 'r-tray',
    panel: 'r-panel',
  },
  shadows: {
    light: {
      'sh-stone': [
        { x: 0, y: 1, blur: 0, spread: 0, color: 'fixed.shadow-ink/0.05' },
        { x: 0, y: 14, blur: 30, spread: -18, color: 'fixed.shadow-ink/0.45' },
      ],
      'sh-float': [{ x: 0, y: 14, blur: 30, spread: -14, color: 'fixed.shadow-ink/0.6' }],
      'shadow-sm': [
        { x: 0, y: 1, blur: 0, spread: 0, color: 'fixed.shadow-ink/0.05' },
        { x: 0, y: 8, blur: 18, spread: -14, color: 'fixed.shadow-ink/0.4' },
      ],
      'ring-now': [{ x: 0, y: 0, blur: 0, spread: 3, color: 'fixed.sun' }],
      'ring-sel': [{ x: 0, y: 0, blur: 0, spread: 3, color: 'brand.primary' }], // a selected item (not "now")
    },
    dark: {
      'sh-stone': [
        { x: 0, y: 1, blur: 0, spread: 0, color: 'fixed.black/0.3' },
        { x: 0, y: 14, blur: 30, spread: -18, color: 'fixed.black/0.7' },
      ],
      'sh-float': [{ x: 0, y: 14, blur: 30, spread: -14, color: 'fixed.black/0.8' }],
      'shadow-sm': [{ x: 0, y: 8, blur: 18, spread: -14, color: 'fixed.black/0.7' }],
      'ring-now': [{ x: 0, y: 0, blur: 0, spread: 3, color: 'fixed.sun' }],
      'ring-sel': [{ x: 0, y: 0, blur: 0, spread: 3, color: 'brand.primary' }], // a selected item (not "now")
    },
  },
  elevation: {
    resting: 'sh-stone',
    floating: 'sh-float',
    subtle: 'shadow-sm',
    now: 'ring-now',
    selected: 'ring-sel',
  },
  spacing: { '0': 0, '1': 4, '2': 8, '3': 12, '4': 16, '5': 20, '6': 24, '8': 32 },
  touch: { minTarget: 44, primaryTarget: 60 },
  // One motion value in the spec: 160 ms ease-out for state changes; nothing animates on load.
  motion: {
    durations: { instant: 0, fast: 160, normal: 160, slow: 160 },
    easings: { standard: [0, 0, 0.58, 1], enter: [0, 0, 0.58, 1], exit: [0, 0, 0.58, 1] },
  },
  // The office app's shared stylesheets (office.ts): their measures, which Blueprint's set carries too.
  // Riverstone has no base scales of its own beyond spacing and type, so these are its only layers.
  meaning: officeMeaning('riverstone'),
  components: { ...OFFICE_COMPONENTS },
};

/** The token set the product uses unless told otherwise (decision 16). */
export const defaultTokenSet = riverstone;
export const defaultBrand = RIVERSTONE_DEFAULT_BRAND;
